import { useQuery } from '@tanstack/react-query'
import Spinner from 'react-bootstrap/Spinner'
import { Link } from 'react-router-dom'
import { getMemberDebts, type MemberDebtLine } from '../../../../types'
import { actionApiCall } from '../../api/actionApiCall'
import { useCurrencies } from '../../hooks/useCurrencies'
import { formatDate } from '../../utils/format'
import { exponentOf, formatMoney } from '../../utils/money'

/** Pohyby člena - revize skupiny v klíči zajistí, že po každém zápisu přijdou čerstvé. */
function useMemberDebts(groupId: string, memberId: string, revision: number) {
    return useQuery({
        queryKey: ['dluhy', groupId, memberId, revision],
        queryFn: () => actionApiCall(() => getMemberDebts({ path: { groupId, memberId } }), { silent: true }),
    })
}

type Spolecne = {
    groupId: string
    revision: number
    castka: (hodnota: number) => string
    jmeno: (memberId: string) => string
}

/** Položky účtenky, které na člena připadly. */
function Polozky({ line }: { line: MemberDebtLine }) {
    const { data: currencies } = useCurrencies()
    if (line.items.length === 0) {
        return null
    }
    return (
        <ul className="list-unstyled small text-secondary mb-0 ms-3">
            {line.items.map((item, index) => (
                <li key={index} className="d-flex justify-content-between gap-2">
                    <span className="text-break">
                        {item.quantity !== 1 && `${String(item.quantity).replace('.', ',')}× `}
                        {item.label}
                        {item.sharedWith > 1 && ` (dělí se ${item.sharedWith})`}
                    </span>
                    <span className="text-nowrap">{formatMoney(item.amount, item.currencyCode, exponentOf(currencies, item.currencyCode))}</span>
                </li>
            ))}
        </ul>
    )
}

/** Název pohybu - u útraty s účtenkou odkaz na její přehled. */
function NazevPohybu({ line, groupId }: { line: MemberDebtLine; groupId: string }) {
    // Úhrada se tak většinou i jmenuje - předpona jen tam, kde z názvu není poznat.
    const nazev = line.type === 'TRANSFER' && !/úhrad/i.test(line.title) ? `Úhrada: ${line.title}` : line.title
    return (
        <span className="text-truncate">
            {line.receiptCount > 0 ? <Link to={`/skupiny/${groupId}/pohyby/${line.transactionId}/uctenka`}>{nazev}</Link> : nazev}
            <span className="text-secondary"> · {formatDate(line.occurredAt)}</span>
        </span>
    )
}

function Nacitani() {
    return (
        <div className="small text-secondary py-1">
            <Spinner animation="border" size="sm" className="me-2" />
            Načítám…
        </div>
    )
}

/**
 * Za co dlužník dluží věřiteli - útraty, ze kterých párový převod vznikl.
 * Částky dávají dohromady přesně částku převodu: co věřitel zaplatil za
 * dlužníka, minus co dlužník zaplatil za věřitele a co už mu poslal.
 */
export function ZaCoPrevodu({ fromMemberId, toMemberId, ...spolecne }: Spolecne & { fromMemberId: string; toMemberId: string }) {
    const { groupId, revision, castka, jmeno } = spolecne
    const { data: radky, isLoading } = useMemberDebts(groupId, fromMemberId, revision)

    if (isLoading) {
        return <Nacitani />
    }

    const relevantni = (radky ?? [])
        .map((line) => {
            const dluzi = line.owesTo.find((dil) => dil.memberId === toMemberId)?.amount ?? 0
            const odecte = line.owedBy.find((dil) => dil.memberId === toMemberId)?.amount ?? 0
            return { line, dluzi, odecte }
        })
        .filter(({ dluzi, odecte }) => dluzi !== 0 || odecte !== 0)

    return (
        <div className="w-100 small mt-2" data-testid="za-co-prevodu">
            {relevantni.map(({ line, dluzi, odecte }) => (
                <div key={line.transactionId} className="border-top py-1">
                    <div className="d-flex justify-content-between gap-2">
                        <NazevPohybu line={line} groupId={groupId} />
                        <span className={`text-nowrap ${dluzi - odecte > 0 ? '' : 'text-success'}`}>
                            {dluzi - odecte > 0 ? '+' : '−'} {castka(Math.abs(dluzi - odecte))}
                        </span>
                    </div>
                    {dluzi > 0 && line.type === 'EXPENSE' && (
                        <div className="text-secondary">
                            Zaplatil {jmeno(toMemberId)}
                            {line.items.length > 0 && `, na ${jmeno(fromMemberId)} připadlo:`}
                        </div>
                    )}
                    {dluzi > 0 && <Polozky line={line} />}
                    {odecte > 0 && line.type === 'EXPENSE' && (
                        <div className="text-secondary">Zaplatil {jmeno(fromMemberId)} i za {jmeno(toMemberId)} — odečte se.</div>
                    )}
                </div>
            ))}
            {relevantni.length === 0 && <div className="text-secondary">Žádné pohyby mezi nimi.</div>}
        </div>
    )
}

/** Pohyby člena, ze kterých vzniká jeho bilance, i s položkami účtenek. */
export function ZaCoClena({ memberId, ...spolecne }: Spolecne & { memberId: string }) {
    const { groupId, revision, castka } = spolecne
    const { data: radky, isLoading } = useMemberDebts(groupId, memberId, revision)

    if (isLoading) {
        return <Nacitani />
    }

    return (
        <div className="small mt-2" data-testid="za-co-clena">
            <div className="fw-semibold mb-1">Za co</div>
            {(radky ?? []).map((line) => (
                <div key={line.transactionId} className="border-top py-1">
                    <div className="d-flex justify-content-between gap-2">
                        <NazevPohybu line={line} groupId={groupId} />
                        <span className="text-nowrap text-end">
                            {line.paidBase > 0 && <span className="text-success">+ {castka(line.paidBase)} </span>}
                            {line.shareBase > 0 && <span className="text-danger">− {castka(line.shareBase)}</span>}
                        </span>
                    </div>
                    <Polozky line={line} />
                </div>
            ))}
            {(radky ?? []).length === 0 && <div className="text-secondary">Člen zatím nemá žádné pohyby.</div>}
        </div>
    )
}
